import type { Reservation } from '@luxuryescapes/contract-svc-reservation';

import { ALLOWED_IMAGE_UPLOAD_ACCEPT } from '~/services/ImageService';

import { getRoomKey } from '../utils';

import type { MediaImage, MediaSourceImage, PropertyRoomTypeWithImages } from './types';

export { getRoomKey };

export const ROOM_IMAGE_LIMIT = 5;
export const UNASSIGNED_IMAGE_LABEL = 'Unassigned';
export const DEFAULT_ROOM_NAME = UNASSIGNED_IMAGE_LABEL;
export const DEFAULT_SOURCE_ROOM_NAME = 'Room';
export const HERO_ROOM_NAME = 'Property hero';
const ALLOWED_IMAGE_UPLOAD_TYPES = ALLOWED_IMAGE_UPLOAD_ACCEPT.split(',');

interface UploadRoomAssignment {
  roomKey: string;
  roomName: string;
}

export type UploadedRoomAssignment = UploadRoomAssignment & {
  publicId: string;
};

function getFileExtension(filename: string) {
  return filename.split('.').pop()?.toLowerCase();
}

export function isAllowedImageFile(file: File) {
  const extension = getFileExtension(file.name);

  return (
    ALLOWED_IMAGE_UPLOAD_TYPES.includes(file.type) ||
    ALLOWED_IMAGE_UPLOAD_TYPES.includes(extension ? `.${extension}` : '')
  );
}

function normaliseRoomName(roomName: string) {
  return roomName
    .trim()
    .toLowerCase()
    .replace(/[-_]+/g, ' ')
    .replace(/[^\p{L}\p{N}\s]+/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function getFileBasename(filename: string) {
  return filename.replace(/\.[^/.]+$/, '').trim();
}

function getBestRoomMatch(
  roomNameCandidate: string,
  propertyRoomTypes: Array<PropertyRoomTypeWithImages>,
): UploadRoomAssignment | undefined {
  const normalisedRoomNameCandidate = normaliseRoomName(roomNameCandidate);
  let bestMatch: (UploadRoomAssignment & { score: number }) | undefined;

  propertyRoomTypes.forEach((room, index) => {
    if (!room.name) {
      return;
    }

    const normalisedRoomName = normaliseRoomName(room.name);
    const isExactMatch = normalisedRoomNameCandidate === normalisedRoomName;
    const isPrefixMatch = normalisedRoomNameCandidate.startsWith(`${normalisedRoomName} `);

    if (!isExactMatch && !isPrefixMatch) {
      return;
    }

    const score = isExactMatch ? Number.MAX_SAFE_INTEGER : normalisedRoomName.length;

    if (!bestMatch || score > bestMatch.score) {
      bestMatch = {
        roomKey: getRoomKey(room, index),
        roomName: room.name,
        score,
      };
    }
  });

  return bestMatch ? { roomKey: bestMatch.roomKey, roomName: bestMatch.roomName } : undefined;
}

export function getUploadRoomMatch(
  filePath: string,
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> = [],
): { roomKey: string; roomName: string } | undefined {
  const pathParts = filePath.split('/').filter(Boolean);

  if (pathParts.length === 0) {
    return undefined;
  }

  const roomNameCandidates = pathParts.map((pathPart, index) =>
    index === pathParts.length - 1 ? getFileBasename(pathPart) : pathPart,
  );

  for (const roomNameCandidate of roomNameCandidates) {
    const roomMatch = getBestRoomMatch(roomNameCandidate, propertyRoomTypes);

    if (roomMatch) {
      return roomMatch;
    }
  }

  return undefined;
}

export function buildRoomImageCounts(
  propertyRoomTypes: Array<PropertyRoomTypeWithImages> = [],
  excludedImagePublicIds: Array<string> = [],
) {
  const excludedPublicIds = new Set(excludedImagePublicIds);

  return propertyRoomTypes.reduce<Map<string, number>>((roomImageCounts, room, index) => {
    const imageCount = (room.images ?? []).filter(
      (image) => image.id_cloudinary_external && !excludedPublicIds.has(image.id_cloudinary_external),
    ).length;

    roomImageCounts.set(getRoomKey(room, index), Math.min(imageCount, ROOM_IMAGE_LIMIT));
    return roomImageCounts;
  }, new Map());
}

export function buildUploadPlans({
  deletedImagePublicIds,
  files,
  imageSourceLabelOverrides,
  propertyRoomTypes,
  uploadedRoomAssignments,
}: {
  deletedImagePublicIds: Array<string>;
  files: Array<File>;
  imageSourceLabelOverrides: Record<string, string>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
  uploadedRoomAssignments: Array<UploadedRoomAssignment>;
}) {
  const deletedImagePublicIdSet = new Set(deletedImagePublicIds);
  const roomImageCounts = buildRoomImageCounts(
    propertyRoomTypes,
    deletedImagePublicIds.concat(Object.keys(imageSourceLabelOverrides)),
  );
  const addRoomImageCount = (roomKey: string) => {
    roomImageCounts.set(roomKey, Math.min((roomImageCounts.get(roomKey) ?? 0) + 1, ROOM_IMAGE_LIMIT));
  };

  Object.entries(imageSourceLabelOverrides).forEach(([publicId, sourceLabel]) => {
    if (deletedImagePublicIdSet.has(publicId) || sourceLabel === UNASSIGNED_IMAGE_LABEL) {
      return;
    }

    const roomMatch = getBestRoomMatch(sourceLabel, propertyRoomTypes ?? []);

    if (!roomMatch) {
      return;
    }

    addRoomImageCount(roomMatch.roomKey);
  });

  uploadedRoomAssignments.forEach((assignment) => {
    if (deletedImagePublicIdSet.has(assignment.publicId) || imageSourceLabelOverrides[assignment.publicId]) {
      return;
    }

    addRoomImageCount(assignment.roomKey);
  });

  return files.map((file) => {
    const roomMatch = getUploadRoomMatch(file.webkitRelativePath || file.name, propertyRoomTypes);
    const roomImageCount = roomMatch ? (roomImageCounts.get(roomMatch.roomKey) ?? 0) : 0;
    const roomAssignment = roomMatch && roomImageCount < ROOM_IMAGE_LIMIT ? roomMatch : undefined;

    if (roomAssignment) {
      roomImageCounts.set(roomAssignment.roomKey, roomImageCount + 1);
    }

    return {
      file,
      roomAssignment,
    };
  });
}

export function dedupeMediaImages(images: Array<MediaImage>) {
  const seenPublicIds = new Set<string>();

  return images.filter((image) => {
    if (seenPublicIds.has(image.publicId)) {
      return false;
    }

    seenPublicIds.add(image.publicId);
    return true;
  });
}

export function getSyncedImagePublicIds(currentPublicIds: Array<string>, images: Array<MediaImage>) {
  const nextPublicIds = images.map((image) => image.publicId);
  const nextPublicIdSet = new Set(nextPublicIds);
  const existingPublicIds = currentPublicIds.filter((publicId) => nextPublicIdSet.has(publicId));
  const existingPublicIdSet = new Set(existingPublicIds);
  const newPublicIds = nextPublicIds.filter((publicId) => !existingPublicIdSet.has(publicId));

  return existingPublicIds.concat(newPublicIds);
}

function getSourceImages({
  images,
  seenPublicIds,
  sourceLabel,
}: {
  images?: Array<MediaSourceImage>;
  seenPublicIds: Set<string>;
  sourceLabel: string;
}): Array<MediaImage> {
  return (images ?? []).reduce<Array<MediaImage>>((mediaImages, image) => {
    if (!image.id_cloudinary_external || seenPublicIds.has(image.id_cloudinary_external)) {
      return mediaImages;
    }

    seenPublicIds.add(image.id_cloudinary_external);
    mediaImages.push({
      publicId: image.id_cloudinary_external,
      sourceLabel,
      title: image.title || undefined,
    });

    return mediaImages;
  }, []);
}

export function buildMediaImages({
  offerImages,
  propertyImages,
  propertyRoomTypes,
}: {
  offerImages: Array<App.OfferImage>;
  propertyImages?: Array<Reservation.Image>;
  propertyRoomTypes?: Array<PropertyRoomTypeWithImages>;
}) {
  const seenPublicIds = new Set<string>();
  const heroImages = [offerImages, propertyImages].flatMap((images) =>
    getSourceImages({
      images,
      seenPublicIds,
      sourceLabel: HERO_ROOM_NAME,
    }),
  );
  const roomImages = (propertyRoomTypes ?? []).flatMap((room) =>
    getSourceImages({
      images: room.images,
      seenPublicIds,
      sourceLabel: room.name ? `${room.name}` : DEFAULT_SOURCE_ROOM_NAME,
    }),
  );

  return heroImages.concat(roomImages);
}

export function getPrimaryVideoId(videos?: { luxuryescapes?: { vimeo_id?: string | null } }) {
  const luxuryEscapesVideoId = videos?.luxuryescapes?.vimeo_id;

  return luxuryEscapesVideoId && luxuryEscapesVideoId !== 'undefined' ? luxuryEscapesVideoId : undefined;
}

export function togglePublicId(publicIds: Array<string>, publicId: string) {
  return publicIds.includes(publicId)
    ? publicIds.filter((currentPublicId) => currentPublicId !== publicId)
    : publicIds.concat(publicId);
}

export function setFolderInputRef(node: HTMLInputElement | null) {
  node?.setAttribute('directory', '');
  node?.setAttribute('webkitdirectory', '');
}

export async function allSettledWithConcurrency<TInput, TResult>(
  items: Array<TInput>,
  concurrency: number,
  handler: (item: TInput) => Promise<TResult>,
): Promise<Array<PromiseSettledResult<TResult>>> {
  const results: Array<PromiseSettledResult<TResult>> = [];
  const batchSize = Math.max(1, concurrency);

  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    const batchResults = await Promise.allSettled(batch.map(handler));

    results.push(...batchResults);
  }

  return results;
}
